import { useMemo } from "react";
import type { ServiceTemplateSchedule } from "@common/types/serviceTemplateSchedule";
import { useCloseTemplates } from "@api/closeTemplate.api";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import {
    type UseServiceTemplateSchedulesOptions,
    useServiceTemplateSchedules,
} from "@api/serviceTemplateSchedule.api";
import { useWorkspacesList } from "@api/workspace.api";
import type { DataGridStatus } from "@design-system/DataGrid";
import { useScope } from "@state/scopeContext.lib";
import type { ServiceTemplateScheduleDisplayStatus } from "../serviceTemplateScheduleDisplayStatus";
import {
    groupServiceTemplateSchedules,
    type ServiceTemplateScheduleEntityInfo,
    type ServiceTemplateScheduleRow,
} from "../serviceTemplateSchedulesViewConfig";
import type { TemplateEstimateStats } from "../templateEstimateStats";
import {
    type EngagementTeamMember,
    getScheduleDisplayStatus,
    type ScheduleListLookups,
    type ScheduleListWorkspaceInfo,
} from "./scheduleListValues";

/** The server-side filters and sort the schedules query takes. */
export type ScheduleListQuery = Omit<
    UseServiceTemplateSchedulesOptions,
    "organizationId" | "enabled"
>;

interface UseScheduleListDataParams {
    query: ScheduleListQuery;
    /** Display statuses to keep; empty keeps every schedule. */
    statusFilter: readonly ServiceTemplateScheduleDisplayStatus[];
}

const LOADING_STATUS: DataGridStatus = { state: "loading" };
const READY_STATUS: DataGridStatus = { state: "ready" };
const REFETCHING_STATUS: DataGridStatus = {
    state: "ready",
    isRefetching: true,
};

const useMemberById = () => {
    const { data: organizationMembers } = useCurrentOrganizationMembers();
    return useMemo(
        () =>
            new Map<number, EngagementTeamMember>(
                (organizationMembers ?? []).map(({ user }) => [
                    user.id,
                    {
                        id: user.id,
                        name: user.name,
                        preferredName: user.preferredName,
                        avatar: user.avatar,
                        iconColor: user.iconColor,
                        email: user.email,
                    },
                ]),
            ),
        [organizationMembers],
    );
};

const useTemplateLookups = () => {
    // Every template, archived ones too: schedules never persist archived,
    // so their display status reads it from the template (PRO-10571).
    const templatesQuery = useCloseTemplates({ includeArchived: true });
    const { data: templates } = templatesQuery;
    const lookups = useMemo(() => {
        const templateNameById = new Map<string, string>();
        const templateEstimateById = new Map<string, TemplateEstimateStats>();
        const archivedTemplateIds = new Set<string>();
        for (const template of templates ?? []) {
            templateNameById.set(template.id, template.name);
            templateEstimateById.set(template.id, {
                totalEstimatedTime: template.totalEstimatedTime,
                estimatedTimeByRole: template.estimatedTimeByRole,
            });
            if (template.archivedAt != null) {
                archivedTemplateIds.add(template.id);
            }
        }
        return { templateNameById, templateEstimateById, archivedTemplateIds };
    }, [templates]);
    return { templatesQuery, lookups };
};

const useWorkspaceLookups = (organizationId: string | null | undefined) => {
    const workspacesQuery = useWorkspacesList(
        organizationId ? { organizationId, includeEntities: true } : undefined,
    );
    const { data: workspaces } = workspacesQuery;
    const lookups = useMemo(() => {
        const entityInfoByUuid = new Map<
            string,
            ServiceTemplateScheduleEntityInfo
        >();
        const workspaceInfoByUuid = new Map<
            string,
            ScheduleListWorkspaceInfo
        >();
        for (const workspace of workspaces ?? []) {
            workspaceInfoByUuid.set(workspace.id, {
                id: workspace.id,
                name: workspace.name,
                iconColor: workspace.iconColor,
                timezone: workspace.timezone,
            });
            for (const entity of workspace.entities ?? []) {
                if (entity.uuid) {
                    entityInfoByUuid.set(entity.uuid, {
                        id: entity.id,
                        uuid: entity.uuid,
                        name: entity.name ?? "-",
                        iconColor: entity.iconColor,
                        logo: entity.logo,
                        workspaceId: entity.workspaceId,
                    });
                }
            }
        }
        return { entityInfoByUuid, workspaceInfoByUuid };
    }, [workspaces]);
    return { workspacesQuery, lookups };
};

interface FilterSchedulesArgs {
    schedules: ServiceTemplateSchedule[];
    statusFilter: readonly ServiceTemplateScheduleDisplayStatus[];
    lookups: ScheduleListLookups;
}

/** Runs before grouping, so a hidden schedule never counts toward its group. */
const filterByStatus = ({
    schedules,
    statusFilter,
    lookups,
}: FilterSchedulesArgs) =>
    statusFilter.length === 0
        ? schedules
        : schedules.filter((schedule) =>
              statusFilter.includes(
                  getScheduleDisplayStatus({
                      row: { ...schedule, rowType: "schedule" },
                      lookups,
                  }),
              ),
          );

/**
 * The Schedules list's rows and the lookups their cells read. Rows wait for
 * schedules, workspaces, and templates together, so no cell shows names
 * before they resolve (PRO-10674) and the status filter never reads empty
 * lookups as deleted or archived.
 */
export const useScheduleListData = ({
    query,
    statusFilter,
}: UseScheduleListDataParams) => {
    const { organizationId } = useScope();
    const schedulesQuery = useServiceTemplateSchedules({
        organizationId,
        ...query,
    });
    const memberById = useMemberById();
    const templates = useTemplateLookups();
    const workspaces = useWorkspaceLookups(organizationId);
    const lookups = useMemo<ScheduleListLookups>(
        () => ({ ...templates.lookups, ...workspaces.lookups, memberById }),
        [templates.lookups, workspaces.lookups, memberById],
    );

    const queries = [
        schedulesQuery,
        templates.templatesQuery,
        workspaces.workspacesQuery,
    ];
    const isLoaded = queries.every(({ isFetched }) => isFetched);
    const failedRefetches = queries.flatMap(({ isLoadingError, refetch }) =>
        isLoadingError ? [refetch] : [],
    );

    const { data: schedules } = schedulesQuery;
    const filteredSchedules = useMemo(
        () =>
            isLoaded && schedules
                ? filterByStatus({ schedules, statusFilter, lookups })
                : null,
        [isLoaded, schedules, statusFilter, lookups],
    );
    const rows = useMemo<ServiceTemplateScheduleRow[] | null>(
        () =>
            filteredSchedules
                ? groupServiceTemplateSchedules(filteredSchedules)
                : null,
        [filteredSchedules],
    );
    const schedulesById = useMemo(
        () =>
            new Map(
                (schedules ?? []).map((schedule) => [schedule.id, schedule]),
            ),
        [schedules],
    );

    let status: DataGridStatus = READY_STATUS;
    if (failedRefetches.length > 0) {
        status = {
            state: "error",
            onRetry: () => failedRefetches.forEach((refetch) => void refetch()),
        };
    } else if (!rows) {
        status = LOADING_STATUS;
    } else if (schedulesQuery.isRefetching) {
        status = REFETCHING_STATUS;
    }

    return {
        rows,
        scheduleCount: filteredSchedules?.length ?? 0,
        schedulesById,
        lookups,
        status,
    };
};
